import React from 'react'
import { Link, Outlet, useParams } from 'react-router-dom'
import { Backdrop, styled, Typography, Stack, Button } from '@mui/material'
import Divider from '@mui/material/Divider'

//components
import MenuItem from './MenuItem'
import RefreshDataButton from '../components/RefreshData/RefreshDataButton'

//assets
import Logo from '../assets/logo.png'
import { SIDE_PANEL_WIDTH } from '../utils/constants'

import HomeIcon from '@mui/icons-material/Home'
import BackupTableIcon from '@mui/icons-material/BackupTable'
import RssFeedIcon from '@mui/icons-material/RssFeed'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import BarChartIcon from '@mui/icons-material/BarChart'
import EngineeringIcon from '@mui/icons-material/Engineering'
import AssessmentIcon from '@mui/icons-material/Assessment'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'

import DashboardGlobalStateContext from '../context/DashboardGlobalStateContext'
import LoadingSpinner from '../components/LoadingSpinner'
import { useAuth0 } from '@auth0/auth0-react'

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'rgba(0,0,0,0.1)',
  color: theme.palette.secondary.main
}))

const App = styled('main')(() => ({
  width: '100%',
  height: '100vh',
  display: 'flex',
  color: '#424242',
  backgroundColor: '#fff'
}))

const SidePanel = styled('div')(() => ({
  width: `${SIDE_PANEL_WIDTH}px`,
  height: '100vh',
  borderRight: '1px solid #dedede',
  backgroundColor: '#113969'
}))

const SidePanelInnerDiv = styled('div')(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px'
}))

const MainContent = styled('div')(() => ({
  width: `calc(100% - ${SIDE_PANEL_WIDTH}px)`,
  width: '100%',
  height: '100vh',
  overflowY: 'hidden'
}))

const LogoWrapper = styled('img')(() => ({
  maxWidth: '100%',
  borderRadius: '8px'
}))

const LogoBox = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '0 16px',
  marginTop: 'auto'
}))

const NavBox = styled('div')(() => ({
  display: 'flex',
  gap: '8px',
  flexDirection: 'column',
  marginTop: '12px'
}))

const DashboardLayout = () => {
  const { unit } = useParams() 
  const { logout } = useAuth0()

  const [machinesList, setMachinesList] = React.useState([])
  const [fetchingMachineState, setFetchingMachineState] = React.useState('idle')

  const SidebarData = [
    {
      title: 'Home',
      path: `/${unit}`,
      renderIcon: () => <HomeIcon fontSize="small" />
    },
    {
      title: 'Dashboard',
      path: `/${unit}/dashboard`,
      renderIcon: () => <BackupTableIcon fontSize="small" />
    },
    {
      title: 'Live data',
      path: `/${unit}/dashboard/live-data`,
      renderIcon: () => <RssFeedIcon fontSize="small" />
    },
    {
      title: 'Machine wise',
      path: `/${unit}/dashboard/machine-wise`,
      renderIcon: () => <PrecisionManufacturingIcon fontSize="small" />
    },
    {
      title: 'Power BI',
      path: `/${unit}/dashboard/power-bi-report`,
      renderIcon: () => <AssessmentIcon fontSize="small" />,
    //   onClick: () => {
    //     const redirectUrl = `${process.env.REACT_APP_DATA_LOGGER_BASE_URL}/v1/power-bi-report/redirect`
    //     window.open(redirectUrl, '_blank')
    //   }
    }
  ]

  const historicalData = [
    // {
    //   title: 'Historical',
    //   path: `/${unit}/dashboard/historical`,
    //   renderIcon: () => <BarChartIcon fontSize="medium" />
    // },
    {
      title: 'Operator info',
      path: `/${unit}/dashboard/operator-info`,
      renderIcon: () => <EngineeringIcon fontSize="medium" />
    }
  ]

  React.useEffect(() => {
    setFetchingMachineState('fetching')
    fetch(`${process.env.REACT_APP_ADMIN_BASE_URL}/v1/machines`)
      .then((res) => res.json())
      .then((data) => {
        const machineOptions = data.data.map((item) => ({
          value: item.name,
          label: item.displayName
        }))
        setMachinesList(machineOptions)
      })
      .catch(() => setFetchingMachineState('failed'))
      .finally(() => setFetchingMachineState('finished'))
  }, [])

  const loadingState = ['idle', 'fetching'].includes(fetchingMachineState)

  if (loadingState) {
    return <LoadingSpinner loading text="Loading data..." />
  }

  if (!loadingState && machinesList.length === 0) {
    return (
      <StyledBackdrop open>
        <Stack sx={{ padding: '24px', borderRadius: '8px', gap: '12px' }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 48, color: 'red' }} />
          <Typography>Something went wrong</Typography>
          <Stack direction="row" gap={2}>
            <Button onClick={() => window.location.reload()}>Refresh</Button>
            <Button
              onClick={() => logout({ returnTo: window.location.origin })}
            >
              Logout
            </Button>
          </Stack>
        </Stack>
      </StyledBackdrop>
    )
  }

  return (
    <DashboardGlobalStateContext.Provider value={{ machines: machinesList }}>
      <App>
        <SidePanel>
          <SidePanelInnerDiv>
            <NavBox>
              {SidebarData.map((item, i) => (
                <MenuItem key={i} item={item} />
              ))}
            </NavBox>

            <Divider sx={{ my: 1, backgroundColor: 'gray' }} />

            <NavBox>
              {historicalData.map((item, i) => (
                <MenuItem key={i} item={item} />
              ))}
            </NavBox>

            <Divider sx={{ my: 1, backgroundColor: 'gray' }} />

            <NavBox>
              <RefreshDataButton onRefresh={() => window.location.reload()} />
            </NavBox>

            <LogoBox>
              <Link to={`/${unit}`}>
                <LogoWrapper src={Logo} alt="company logo" />
              </Link>
            </LogoBox>
          </SidePanelInnerDiv>
        </SidePanel>

        <MainContent>
          <Outlet />
        </MainContent>
      </App>
    </DashboardGlobalStateContext.Provider>
  )
}

export default DashboardLayout
